import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { useAuth } from '../../hooks/useAuth';

/**
 * TwoFactorChallenge — modal presented after password authentication
 * when a user's Cognito ID token carries `custom:twofa_method=email_magic_link`.
 *
 * Behavior
 * --------
 * The clinician sees "Check your email", types the 6-digit verification code
 * we sent her, and clicks Verify. autoComplete="one-time-code" lets iOS
 * and Android surface the code from the email above the keyboard.
 *
 * Error states
 * ------------
 * - 401 TWOFA_INVALID_CODE: stay on form, soft warning beneath the input.
 *   The wording reminds her a fresh sign-in attempt invalidates the
 *   previous code, which is the most common real-world failure.
 * - 403 TWOFA_LOCKED: replace the form with a calm "deactivated for
 *   security" message and a Sign in button. We never use the phrase
 *   "too many incorrect attempts" — it reads as personal failure.
 * - 410 TWOFA_TICKET_EXPIRED (from resend): show "session expired" with
 *   a Sign in button.
 *
 * Mounted unconditionally by AuthProvider; self-gates on twofa.status so
 * a "cold" mount renders nothing.
 */

const COLOR_BRAND = '#2D7D46';
const COLOR_BRAND_DARK = '#1E6B35';
const COLOR_TEXT_PRIMARY = '#1F2937';
const COLOR_TEXT_SECONDARY = '#4B5563';
const COLOR_TEXT_MUTED = '#6B7280';
const COLOR_WARNING = '#B45309';

type LocalState = 'form' | 'locked' | 'expired';

export default function TwoFactorChallenge() {
  const navigate = useNavigate();
  const { twofa, verifyTwoFaCode, resendTwoFaCode, cancelTwoFa } = useAuth();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [inlineMessage, setInlineMessage] = useState<string | null>(null);
  const [inlineWarning, setInlineWarning] = useState<string | null>(null);
  const [localState, setLocalState] = useState<LocalState>('form');
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Reset local state every time the modal transitions from idle/closed
  // back to a fresh pending challenge.
  useEffect(() => {
    if (twofa.status === 'pending' && localState !== 'form') {
      setLocalState('form');
      setCode('');
      setInlineMessage(null);
      setInlineWarning(null);
    }
  }, [twofa.status, localState]);

  // Autofocus the input when the form first appears.
  useEffect(() => {
    if (twofa.status === 'pending' && localState === 'form') {
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [twofa.status, localState]);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (submitting || code.length !== 6) return;
    setSubmitting(true);
    setInlineMessage(null);
    setInlineWarning(null);
    try {
      await verifyTwoFaCode(code);
      // Success — AuthProvider flips user; modal unmounts on re-render.
    } catch (err: any) {
      const status = err?.status as number | undefined;
      if (status === 403) {
        setLocalState('locked');
      } else if (status === 410) {
        setLocalState('expired');
      } else if (status === 401) {
        setInlineWarning(
          "That code didn't match. Double-check the most recent email — codes can only be used once, and a new sign-in attempt invalidates the previous code.",
        );
        setCode('');
        setTimeout(() => inputRef.current?.focus(), 0);
      } else {
        setInlineWarning(err?.message || 'Verification failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resending) return;
    setResending(true);
    setInlineMessage(null);
    setInlineWarning(null);
    try {
      await resendTwoFaCode();
      setInlineMessage('New code sent — check your email.');
      setCode('');
      setTimeout(() => inputRef.current?.focus(), 0);
    } catch (err: any) {
      const status = err?.status as number | undefined;
      if (status === 410) {
        setLocalState('expired');
      } else if (status === 403) {
        setLocalState('locked');
      } else {
        setInlineWarning(err?.message || "We couldn't send a new code. Please try again.");
      }
    } finally {
      setResending(false);
    }
  };

  const handleSignIn = async () => {
    setCanceling(true);
    try {
      await cancelTwoFa();
      navigate('/login', { replace: true });
    } finally {
      setCanceling(false);
    }
  };

  const handleCancel = async () => {
    setCanceling(true);
    try {
      await cancelTwoFa();
    } finally {
      setCanceling(false);
    }
  };

  // Render nothing in the default-off / signed-in case. While the
  // challenge is pending we keep the modal mounted regardless of
  // localState, since lockout/expired views live on top of the same
  // pending challenge.
  if (twofa.status !== 'pending') {
    return null;
  }

  return (
    <Dialog
      open
      disableEscapeKeyDown
      aria-labelledby="twofa-challenge-title"
      PaperProps={{ sx: { borderRadius: 3, maxWidth: 480, width: '100%' } }}
    >
      <DialogTitle id="twofa-challenge-title" sx={{ pt: 4, pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <MailOutlineIcon sx={{ color: COLOR_BRAND }} fontSize="large" />
          <Typography variant="h6" component="span" fontWeight={600}>
            Check your email
          </Typography>
        </Box>
      </DialogTitle>

      {localState === 'form' && (
        <>
          <DialogContent sx={{ pt: 2, pb: 2 }}>
            <Typography variant="body2" sx={{ color: COLOR_TEXT_SECONDARY, mb: 1 }}>
              We sent a verification code to
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: COLOR_TEXT_PRIMARY,
                fontWeight: 600,
                mb: 3,
                wordBreak: 'break-all',
              }}
            >
              {twofa.email || 'your email address'}
            </Typography>

            <form onSubmit={handleSubmit}>
              <TextField
                inputRef={inputRef}
                fullWidth
                label="Verification code"
                value={code}
                onChange={(e) => {
                  const next = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setCode(next);
                  if (inlineWarning) setInlineWarning(null);
                }}
                disabled={submitting}
                inputProps={{
                  inputMode: 'numeric',
                  autoComplete: 'one-time-code',
                  pattern: '\\d{6}',
                  maxLength: 6,
                  'aria-label': 'Verification code',
                  style: { letterSpacing: '0.4em', fontSize: 22, textAlign: 'center' },
                }}
                sx={{ mb: 1 }}
              />

              {inlineWarning && (
                <Typography
                  variant="body2"
                  role="alert"
                  sx={{ color: COLOR_WARNING, mt: 1, mb: 1, lineHeight: 1.5 }}
                >
                  {inlineWarning}
                </Typography>
              )}

              {inlineMessage && (
                <Typography
                  variant="body2"
                  role="status"
                  sx={{ color: COLOR_BRAND_DARK, mt: 1, mb: 1 }}
                >
                  {inlineMessage}
                </Typography>
              )}

              <Box sx={{ mt: 3 }}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={submitting || code.length !== 6}
                  sx={{
                    py: 1.5,
                    bgcolor: COLOR_BRAND,
                    fontWeight: 600,
                    fontSize: 15,
                    textTransform: 'none',
                    borderRadius: 2,
                    '&:hover': { bgcolor: COLOR_BRAND_DARK },
                    '&.Mui-disabled': { bgcolor: '#E5E7EB', color: COLOR_TEXT_MUTED },
                  }}
                  endIcon={
                    submitting ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null
                  }
                >
                  {submitting ? 'Verifying…' : 'Verify'}
                </Button>
              </Box>

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button
                  type="button"
                  onClick={handleResend}
                  disabled={resending}
                  variant="text"
                  sx={{
                    color: COLOR_BRAND,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 14,
                  }}
                >
                  {resending ? 'Sending…' : "Didn't get the email? Resend code"}
                </Button>
              </Box>
            </form>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={handleCancel}
              disabled={canceling}
              sx={{ color: COLOR_TEXT_MUTED, textTransform: 'none' }}
            >
              {canceling ? 'Canceling…' : 'Cancel'}
            </Button>
          </DialogActions>
        </>
      )}

      {localState === 'locked' && (
        <>
          <DialogContent sx={{ pt: 2, pb: 3 }}>
            <Typography variant="body1" sx={{ color: COLOR_TEXT_PRIMARY, mb: 1 }}>
              For security, this code has been deactivated.
            </Typography>
            <Typography variant="body2" sx={{ color: COLOR_TEXT_SECONDARY }}>
              Click &ldquo;Sign in&rdquo; below to receive a fresh code.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={handleSignIn}
              disabled={canceling}
              fullWidth
              variant="contained"
              sx={{
                py: 1.5,
                bgcolor: COLOR_BRAND,
                fontWeight: 600,
                fontSize: 15,
                textTransform: 'none',
                borderRadius: 2,
                '&:hover': { bgcolor: COLOR_BRAND_DARK },
              }}
            >
              {canceling ? 'Returning…' : 'Sign in'}
            </Button>
          </DialogActions>
        </>
      )}

      {localState === 'expired' && (
        <>
          <DialogContent sx={{ pt: 2, pb: 3 }}>
            <Typography variant="body1" sx={{ color: COLOR_TEXT_PRIMARY, mb: 1 }}>
              Sign-in session expired.
            </Typography>
            <Typography variant="body2" sx={{ color: COLOR_TEXT_SECONDARY }}>
              Click &ldquo;Sign in&rdquo; below to start fresh.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={handleSignIn}
              disabled={canceling}
              fullWidth
              variant="contained"
              sx={{
                py: 1.5,
                bgcolor: COLOR_BRAND,
                fontWeight: 600,
                fontSize: 15,
                textTransform: 'none',
                borderRadius: 2,
                '&:hover': { bgcolor: COLOR_BRAND_DARK },
              }}
            >
              {canceling ? 'Returning…' : 'Sign in'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
